import { useComputed } from 'owa-react-hooks/lib/useComputed';
import { previewPaneEllipsisAriaLabelText } from './SxSView.locstring.json';
import loc from 'owa-localize';
import { observer as observerLite } from 'mobx-react-lite';
import { FilterableCommandBar, FilterableMenuItem } from 'owa-filterable-menu';
import { useKeydownHandler } from 'owa-hotkeys';
import {
    lazySubscribeToResizeEvent,
    lazyTriggerResizeEvent,
    lazyUnsubscribeFromResizeEvent,
} from 'owa-resize-event';
import { ResizeHandle } from 'owa-resize-handle';
import { isShySuiteHeaderMode } from 'owa-suite-header-store';

import {
    ImageGalleryItem,
    lazySetPanesSize,
    lazyUpdatePanesSize,
    LoadedInfo,
    MIN_PREVIEW_PANE_WIDTH,
    RESPONSIVE_READING_PANE_WIDTH,
    SxSViewState,
} from 'owa-sxsdata';
import * as React from 'react';
import { SxSCustomData } from '../utils/combineCustomData';
import { fileNameToOfficeOnlineApp } from '../utils/fileExtensionToOfficeOnlineApp';
import getCommandBarItems from '../utils/getCommandBarItems';
import { getErrorCommandItems } from '../utils/getErrorCommandItems';
import { registerSxSViewStateFromElement } from '../utils/getSxSViewStateFromElement';
import { getSaveStatusText } from '../utils/getSaveStatusText';
import { isSaveStatusSupported } from '../utils/isSaveStatusSupported';
import { PreviewPaneCommandBarItem } from '../utils/PreviewPaneCommandBarButton';
import { ImagePreviewArrowCallbacks } from './ImagePreview';
import PreviewPane, { PreviewPaneHandle } from './PreviewPane';

export interface SxSViewStringOverrides {
    showEmail?: string;
    hideEmail?: string;
}

export interface SxSViewProps {
    children: any;
    className?: string;
    commandBarButtons?: PreviewPaneCommandBarItem[];

    /**
     * getDownloadUrl is for scenarios that download URL changes
     * consumer should still set SxSViewState.previewPane.downloadUrl
     * to make the download button show and as a fallback
     */
    getDownloadUrl?: () => string;

    getCustomPropertiesForSigs?: null | (() => { FileType: string; Sender: string });
    getExtraCustomDataForDatapoint?: () => SxSCustomData;
    getItemIdForLogging?: null | (() => string);
    getOpenCloudyDocumentInNewTabButton?: null | (() => FilterableMenuItem);
    hideCloseButton?: boolean;
    imagePreviewArrowCallbacks?: ImagePreviewArrowCallbacks;
    isInPopOut?: boolean;

    /**
     * For consumer to update preview url if needed
     */
    onBeforeImageGalleryItemSelected?: (imageGalleryItem: ImageGalleryItem) => Promise<void>;
    onClose?: () => void;

    /**
     * Deprecated - use onImageGalleryItemSelected instead
     */
    onChange?: () => void;
    onError?: (errorMessage: string, error?: Error) => void;
    onImageGalleryItemSelected?: () => void;
    onLoad?: (loadedInfo?: LoadedInfo) => void;
    onMount?: () => void;
    onPopOut?: () => void;
    onFocusRightHandPane?: () => Promise<void>;
    renderCustomView?: () => JSX.Element;
    stringOverrides?: SxSViewStringOverrides;
    neverUseHighContrastCommandBar?: boolean;
    useFullWidthCommandBar?: boolean;
    viewState: SxSViewState;
}

const styles = require('./SxSView.scss');
const classNames = require('classnames/bind').bind(styles);

export default observerLite(function SxSView(props: SxSViewProps) {
    React.useEffect(() => {
        if (!props.isInPopOut) {
            lazyTriggerResizeEvent.importAndExecute();
            lazySubscribeToResizeEvent.importAndExecute(onOwaResize);
        }
        if (props.onMount) {
            props.onMount();
        }
        return () => {
            if (props.isInPopOut) {
                if (parentElement.current?.ownerDocument?.defaultView) {
                    parentElement.current.ownerDocument.defaultView.removeEventListener(
                        'resize',
                        onOwaResize
                    );
                }
            } else {
                lazyUnsubscribeFromResizeEvent.importAndExecute(onOwaResize);
            }
            registerSxSViewStateFromElement(currentDivElement.current, null, null);
        };
    }, []);
    React.useEffect(() => {
        const newShowingLeftPane = !!props.viewState.previewPane;
        if (!currentShowingLeftPane.current && newShowingLeftPane) {
            updateSize();
        }
        currentShowingLeftPane.current = newShowingLeftPane;
    });
    const currentDivElement = React.useRef<HTMLDivElement>();
    const parentElement = React.useRef<HTMLElement>();
    const previewPane = React.useRef<PreviewPaneHandle>();
    const currentShowingLeftPane = React.useRef<boolean>();
    const commandBarContainer = React.useRef<HTMLDivElement>();
    const errorCommands = useComputed(() => {
        return getErrorCommandItems(
            props.viewState,
            props.getExtraCustomDataForDatapoint,
            props.getItemIdForLogging,
            props.getDownloadUrl,
            getContainerElementForDownload,
            props.getCustomPropertiesForSigs,
            props.getOpenCloudyDocumentInNewTabButton
        );
    });
    const renderPreviewPane = (
        cssClass: string,
        leftPaneStyles: React.CSSProperties
    ): JSX.Element => {
        const minPreviewPaneWidth = props.viewState.minLeftPaneWidth || MIN_PREVIEW_PANE_WIDTH;
        return (
            <PreviewPane
                className={cssClass}
                commandBar={!props.useFullWidthCommandBar && renderCommandBar()}
                errorCommandItems={errorCommands.get()}
                getExtraCustomDataForDatapoint={props.getExtraCustomDataForDatapoint}
                imagePreviewArrowCallbacks={props.imagePreviewArrowCallbacks}
                onBeforeImageGalleryItemSelected={props.onBeforeImageGalleryItemSelected}
                onError={props.onError}
                onImageGalleryItemSelected={props.onImageGalleryItemSelected || props.onChange}
                onLoad={props.onLoad}
                ref={previewPane}
                renderCustomView={props.renderCustomView}
                style={{ ...leftPaneStyles, minWidth: minPreviewPaneWidth }}
                viewState={props.viewState}
                onFocusCommandBar={setFocusOnCommandBar}
                onFocusRightHandPane={props.onFocusRightHandPane}
            />
        );
    };
    const renderCommandBar = () => {
        const commandBarItems = getCommandBarItems(
            props.viewState,
            getFullScreenElement,
            triggerPreviewPaneResizeEvent,
            pdfJsPrint,
            !!props.children,
            props.hideCloseButton,
            props.commandBarButtons,
            props.onClose,
            props.onPopOut,
            props.getExtraCustomDataForDatapoint,
            props.getItemIdForLogging,
            props.stringOverrides,
            props.getDownloadUrl,
            props.useFullWidthCommandBar,
            getContainerElementForDownload,
            props.getCustomPropertiesForSigs
        );

        const fileName = props.viewState.previewPane?.fileName;
        const officeOnlineAppInfo = fileNameToOfficeOnlineApp(fileName);
        return (
            <div className={styles.commandBarStyleUpdate} ref={commandBarContainer} tabIndex={-1}>
                {officeOnlineAppInfo && (
                    <div
                        className={classNames(
                            styles.officeOnlineBranding,
                            officeOnlineAppInfo.className
                        )}>
                        {officeOnlineAppInfo.displayName}
                    </div>
                )}
                {fileName && <CommandBarFileInfo viewState={props.viewState} />}
                <FilterableCommandBar
                    className={styles.commandBarInner}
                    filterableItems={commandBarItems}
                    elipisisAriaLabel={loc(previewPaneEllipsisAriaLabelText)}
                />
            </div>
        );
    };
    const setFocusOnCommandBar = () => {
        if (commandBarContainer.current) {
            commandBarContainer.current.focus();
        }
    };
    const onHandleResize = async (leftPaneDimension: number, rightPaneDimension: number) => {
        const setPanesSize = await lazySetPanesSize.import();
        setPanesSize(props.viewState, leftPaneDimension, rightPaneDimension);
        await lazyTriggerResizeEvent.importAndExecute();
        triggerPreviewPaneResizeEvent();
    };
    const onOwaResize = () => {
        if (currentShowingLeftPane.current) {
            updateSize();
        }
    };
    const updateParentElement = (div: HTMLDivElement) => {
        if (div) {
            const originalParent = parentElement.current;
            parentElement.current = div.parentElement.parentElement;
            if (props.isInPopOut) {
                if (originalParent?.ownerDocument?.defaultView) {
                    originalParent.ownerDocument.defaultView.removeEventListener(
                        'resize',
                        onOwaResize
                    );
                }
                if (div.ownerDocument?.defaultView) {
                    div.ownerDocument.defaultView.addEventListener('resize', onOwaResize);
                }
            }
        }
        registerSxSViewStateFromElement(currentDivElement.current, div, props.viewState);
        currentDivElement.current = div;
    };
    const updateSize = () => {
        if (parentElement.current) {
            requestAnimationFrame(async () => {
                const updatePanesSize = await lazyUpdatePanesSize.import();
                updatePanesSize(props.viewState, parentElement.current.clientWidth);
                triggerPreviewPaneResizeEvent();
            });
        }
    };
    const triggerPreviewPaneResizeEvent = () => {
        if (previewPane.current) {
            previewPane.current.onResize();
        }
    };
    const getContainerElementForDownload = (): HTMLElement => {
        return currentDivElement.current;
    };
    const getFullScreenElement = (): HTMLElement => {
        return previewPane.current && previewPane.current.getFullScreenElement();
    };
    const pdfJsPrint = () => {
        previewPane.current && previewPane.current.pdfJsPrint();
    };
    useKeydownHandler(currentDivElement, 'escape', (evt: KeyboardEvent) => {
        if (props.onClose) {
            props.onClose();
        }
    });
    const showLeftPane = !!props.viewState.previewPane;
    let hideRightPane = !props.children;
    let mainClasses: string = props.className;
    let leftPaneClasses: string = null;
    let leftPaneStyles: React.CSSProperties = {};
    let rightPaneStyles: React.CSSProperties = {};
    let style = {};
    if (showLeftPane) {
        hideRightPane = hideRightPane || props.viewState.hideRightPane;
        mainClasses = classNames(styles.owaSxS, props.className);
        leftPaneClasses = classNames(
            hideRightPane ? styles.hideRightPane : null,
            styles.owaSxSLeftPane
        );
        if (!hideRightPane && props.viewState.rightPaneSize) {
            leftPaneStyles = { width: props.viewState.leftPaneSizePixels };
            rightPaneStyles = { width: props.viewState.rightPaneSizePixels };
        }
        style = {
            width: props.viewState.leftPaneSizePixels + props.viewState.rightPaneSizePixels,
        };
    }
    const minRightPaneWidth = props.viewState.minRightPaneWidth || RESPONSIVE_READING_PANE_WIDTH;
    return (
        <div
            className={classNames(mainClasses, {
                isShyHeaderMode: isShySuiteHeaderMode(),
            })}
            style={style}
            ref={updateParentElement}>
            {props.useFullWidthCommandBar && renderCommandBar()}
            <div className={styles.owaSxSMain}>
                {showLeftPane && renderPreviewPane(leftPaneClasses, leftPaneStyles)}
                {showLeftPane && !hideRightPane && !props.isInPopOut && (
                    <ResizeHandle onResize={onHandleResize} />
                )}
                {!hideRightPane && (
                    <div
                        className={showLeftPane ? styles.owaSxSRightPane : null}
                        style={{ ...rightPaneStyles, minWidth: minRightPaneWidth }}
                        data-min-width={minRightPaneWidth}>
                        {props.children}
                    </div>
                )}
            </div>
        </div>
    );
});

interface CommandBarFileInfoProps {
    viewState: SxSViewState;
}

const CommandBarFileInfo = observerLite(function CommandBarFileInfo({
    viewState,
}: CommandBarFileInfoProps) {
    const fileName = viewState.previewPane?.fileName;
    const saveStatus = getSaveStatusText(viewState);
    const isStatusSupported = isSaveStatusSupported(viewState);
    const canShowSaveStatus = isStatusSupported && !!saveStatus;

    return (
        <>
            {fileName && (
                <div className={styles.fileName} title={fileName}>
                    {fileName}
                </div>
            )}
            {canShowSaveStatus && (
                <div className={styles.saveStatus} title={saveStatus}>
                    {'- '}
                    {saveStatus}
                </div>
            )}
        </>
    );
});
